import {
  PermissionCategory,
  type Permission,
  PermissionProviderCategory,
  PermissionType,
} from '@repo/api-gw-sdk';

export type PermissionDescriptor = Permission & {
  group?: string;
};

export const ManageBackupSettingsGroup = 'Manage backup settings';

const SearchWorkspaceDataGroup = 'Search and explore workspace data';
const RestoreWorkspaceDataGroup = 'Restore workspace data';

const permissions: Record<PermissionType, { group?: string }> = {
  [PermissionType.GlobalSettingsView]: {},
  [PermissionType.VaultAccountsManage]: {},
  [PermissionType.CostExplorerView]: {},
  [PermissionType.AuditLogsView]: {},
  [PermissionType.UserAccessManage]: {
    group: 'Access management',
  },
  [PermissionType.SamlGroupAccessManage]: {
    group: 'Access management',
  },
  [PermissionType.ApiCredentialsManage]: {
    group: 'Access management',
  },
  [PermissionType.ApiCredentialsRotateSecret]: {
    group: 'Access management',
  },
  [PermissionType.CustomRolesManage]: {
    group: 'Access management',
  },
  [PermissionType.OauthAppsManage]: {
    group: 'Integrations',
  },
  [PermissionType.NotificationPoliciesManage]: {
    group: 'Integrations',
  },
  [PermissionType.PermissionIdUnspecified]: {},
  [PermissionType.DashboardView]: {},
  [PermissionType.InventoryView]: {},
  [PermissionType.SaasInventoryView]: {},
  [PermissionType.JobsView]: {},
  [PermissionType.SaasJobsView]: {},
  [PermissionType.BackupSettingsView]: {},
  [PermissionType.StorageGatewayView]: {},
  [PermissionType.DatabaseExplorerSearch]: {
    group: 'Search and explore',
  },
  [PermissionType.FileExplorerSearch]: {
    group: 'Search and explore',
  },
  [PermissionType.SaasMailSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasUserDriveSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasGroupDataSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasSharedDriveSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasUserSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.FileExplorerDelete]: {},
  [PermissionType.StorageGatewayDataDownload]: {},
  [PermissionType.StorageGatewayMetadataView]: {},
  [PermissionType.DataCatalogView]: {},
  [PermissionType.DataCatalogQuery]: {},
  [PermissionType.SnapshotsTakeOrConvert]: {},
  [PermissionType.SaasSnapshotsTake]: {},
  [PermissionType.ResourcesRestore]: {},
  [PermissionType.ConfigurationValidate]: {},
  [PermissionType.SaasConfigurationValidate]: {},
  [PermissionType.DiscoveryTrigger]: {},
  [PermissionType.FilesRestore]: {
    group: 'Restore granular data',
  },
  [PermissionType.SaasMailRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasUserDriveRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasGroupRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasSharedDriveRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasUserRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.DatabasesQuery]: {
    group: 'Restore granular data',
  },
  [PermissionType.DataClassesOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.EnvironmentsOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.DetectedAppsOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.SensitivityLevelOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.NetworkClassificationOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.ControlViolationsMute]: {},
  [PermissionType.BackupExclusionManage]: {},
  [PermissionType.VaultsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.SourceAccountsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.RestoreAccountsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.BackupPoliciesManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.DiscoveryConfigurationManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.BackupConfigurationManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.ZeroEtlPoliciesManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.ControlsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.RansomwareDetectionManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.SaasBackupScopeManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.StorageGatewayManage]: {
    group: 'Storage gateway',
  },
  [PermissionType.ActionApprovalRulesManage]: {
    group: 'Access management',
  },
  [PermissionType.ActionApprovalRulesView]: {
    group: 'Access management',
  },
  [PermissionType.ActionApprovalRequestsManage]: {},
  [PermissionType.MpaPoliciesManage]: {
    group: 'Access management',
  },
  [PermissionType.MpaPoliciesView]: {
    group: 'Access management',
  },
  [PermissionType.MpaRequestsManage]: {},
  [PermissionType.RestoreTemplatesManage]: {},
  [PermissionType.DatalakeView]: {
    group: 'Managed data lake',
  },
  [PermissionType.DatalakeOperate]: {
    group: 'Managed data lake',
  },
  [PermissionType.ComplianceView]: {},
  [PermissionType.ClassificationPoliciesManage]: {},
};

export const Permissions = Object.fromEntries(
  Object.entries(permissions).filter(
    ([key]) => key !== PermissionType.PermissionIdUnspecified
  )
);

const permissionCategories: Record<PermissionCategory, { title: string }> = {
  [PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION]: {
    title: 'Administration',
  },
  [PermissionCategory.PERMISSION_CATEGORY_GLOBAL_MANAGEMENT]: {
    title: 'Global management',
  },
  [PermissionCategory.PERMISSION_CATEGORY_OPERATION]: {
    title: 'Operation',
  },
  [PermissionCategory.PERMISSION_CATEGORY_VIEWING]: {
    title: 'Viewing',
  },
};

export const PermissionCategories = Object.fromEntries(
  Object.entries(permissionCategories)
);

export enum PermissionFamily {
  GlobalManagement = 'GLOBAL_MANAGEMENT',
  Cloud = 'CLOUD',
  Saas = 'SAAS',
  StorageGateway = 'STORAGE_GATEWAY',
}

// The gateway's permissions are cloud-platform in the catalog — it is backed by S3 and GCS —
// so nothing in providerCategories distinguishes them from the rest of cloud. They are listed
// here because the product treats the gateway as its own surface.
const storageGatewayPermissions = new Set<PermissionType>([
  PermissionType.StorageGatewayView,
  PermissionType.StorageGatewayMetadataView,
  PermissionType.StorageGatewayDataDownload,
  PermissionType.FileExplorerDelete,
  PermissionType.StorageGatewayManage,
]);

export const isStorageGatewayPermission = (permissionType: PermissionType) =>
  storageGatewayPermissions.has(permissionType);

// Global-management permissions carry PERMISSION_CATEGORY_GLOBAL_MANAGEMENT rather than one of
// Viewing/Operation/Administration, so once "Global management" becomes a family chip they have
// no section to sit in. Reclassifying them in the catalog is not an option: projectAdminCategories
// derives the built-in Admin, Operator and Viewer roles from [VIEWING, OPERATION, ADMINISTRATION],
// so moving these would silently grant those roles user-access and custom-role management.
const globalManagementSections: Record<string, PermissionCategory> = {
  [PermissionType.GlobalSettingsView]:
    PermissionCategory.PERMISSION_CATEGORY_VIEWING,
  [PermissionType.AuditLogsView]:
    PermissionCategory.PERMISSION_CATEGORY_VIEWING,
  [PermissionType.CostExplorerView]:
    PermissionCategory.PERMISSION_CATEGORY_VIEWING,
  [PermissionType.ActionApprovalRulesView]:
    PermissionCategory.PERMISSION_CATEGORY_VIEWING,
  [PermissionType.VaultAccountsManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.UserAccessManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.SamlGroupAccessManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.ApiCredentialsManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.ApiCredentialsRotateSecret]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.CustomRolesManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.ActionApprovalRulesManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.NotificationPoliciesManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.OauthAppsManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
};

const familyViewGroups: Record<string, string> = {
  [PermissionType.VaultAccountsManage]: 'Vault accounts',
  [PermissionType.ControlViolationsMute]: 'Resource exceptions',
  [PermissionType.BackupExclusionManage]: 'Resource exceptions',
  [PermissionType.BackupSettingsView]: 'Backup settings',
  [PermissionType.ComplianceView]: 'Backup settings',
  [PermissionType.ClassificationPoliciesManage]: ManageBackupSettingsGroup,
  [PermissionType.RestoreTemplatesManage]: ManageBackupSettingsGroup,
  // The Storage gateway category already says this, so the sub-group only repeats it.
  [PermissionType.StorageGatewayManage]: '',
};

export const getPermissionFamilies = (
  permission: PermissionDescriptor
): PermissionFamily[] => {
  if (
    permission.category ===
    PermissionCategory.PERMISSION_CATEGORY_GLOBAL_MANAGEMENT
  ) {
    return [PermissionFamily.GlobalManagement];
  }

  if (isStorageGatewayPermission(permission.permissionType)) {
    return [PermissionFamily.StorageGateway];
  }

  const declared = permission.providerCategories || [];
  const families: PermissionFamily[] = [];
  // An empty list means unclassified, which the catalog reads as applying to everything —
  // so it must show under both chips rather than vanishing from all of them.
  if (
    declared.length === 0 ||
    declared.includes(
      PermissionProviderCategory.PERMISSION_PROVIDER_CATEGORY_CLOUD_PLATFORM
    )
  ) {
    families.push(PermissionFamily.Cloud);
  }
  if (
    declared.length === 0 ||
    declared.includes(
      PermissionProviderCategory.PERMISSION_PROVIDER_CATEGORY_SAAS
    )
  ) {
    families.push(PermissionFamily.Saas);
  }
  return families;
};

export const getPermissionSection = (
  permission: PermissionDescriptor
): PermissionCategory =>
  globalManagementSections[permission.permissionType] || permission.category;

export const getPermissionFamilyGroup = (permission: PermissionDescriptor) =>
  permission.permissionType in familyViewGroups
    ? familyViewGroups[permission.permissionType]
    : permission.group;
